import { ref, watch } from '@vue/composition-api'
import { debounce } from 'lodash'
import { errorNotification } from '@/utils/toast-notifications'
import axiosIns from '@/libs/axios'

export default function useRewardForm(props, ctx) {
  const roleOptions = [
    {
      label: 'Driver',
      value: 'DRIVER',
    },
    {
      label: 'Rider',
      value: 'USER',
    },
  ]
  const rewardForm = ref({
    name: {
      en: null,
      ar: null,
    },
    expireAt: null,
    effectiveTime: null,
    endTime: null,
    amount: null,
    note: {
      en: '',
      ar: '',
    },
    description: {
      en: null,
      ar: null,
    },
    details: {
      en: null,
      ar: null,
    },
    triggers: [
      'riden.billing.bill-generated',
    ],
    actions: [
      'ADD_TO_GROUP',
      'SEND_NOTIFICATION',
    ],
    group: null,
    targetedGroup: null,
    notification: {
      title: {
        en: null,
        ar: null,
      },
      description: {
        en: null,
        ar: null,
      },
    },
    criteria: {
      role: 'DRIVER',
      type: 'NUMBER_OF_TRIPS',
      daily: false,
      numberOfTrips: null,
      fromHour: null,
      toHour: null,
      fromDate: null,
      toDate: null,
      pickupZone: [],
    },
  })
  const fetchingReward = ref(false)
  const applyWatcher = () => {
    watch(() => rewardForm.value.criteria.daily, () => {
      rewardForm.value.criteria.fromDate = null
      rewardForm.value.criteria.toDate = null
      rewardForm.value.criteria.fromHour = null
      rewardForm.value.criteria.toHour = null
      rewardForm.value.criteria.expireAt = null
    })
  }
  const rewardsFormRef = ref(null)
  const zonesList = ref([])
  const fetchingZones = ref(false)
  const fetchZones = async () => {
    fetchingZones.value = true
    const res = await ctx.root.$store.dispatch('zones/fetchZones')
    fetchingZones.value = false
    zonesList.value = res
  }
  const groupsList = ref([])
  const fetchingGroups = ref(false)

  const fetchGroups = debounce(async (search, loading) => {
    if (!search) return
    loading(true)

    const result = await axiosIns.get('/groups', {
      params: {
        name: search,
      },
    })
    const { data } = result.data.data
    groupsList.value = data
    loading(false)
  }, 1000)

  fetchZones()
  const rewardID = ctx.root.$route.params.id
  const getReward = async () => {
    try {
      fetchingReward.value = true
      const res = await ctx.root.$store.dispatch('rewards/getReward', rewardID)
      rewardForm.value = { ...res.data.data.data }
      rewardForm.value.effectiveTime = new Date(rewardForm.value.effectiveTime)
      rewardForm.value.endTime = new Date(rewardForm.value.endTime)
      if (!rewardForm.value.criteria.daily) {
        rewardForm.value.criteria.fromDate = new Date(rewardForm.value.criteria.fromDate)
        rewardForm.value.criteria.toDate = new Date(rewardForm.value.criteria.toDate)
      }
      applyWatcher()
    } catch (error) {
      errorNotification('An error occured')
    } finally {
      fetchingReward.value = false
    }
  }
  if (rewardID) {
    getReward()
  } else {
    applyWatcher()
  }
  const submitting = ref(false)
  const submitHandler = async () => {
    if (!rewardForm.value.group) {
      errorNotification('The group field is required')
      return
    }
    const valid = await rewardsFormRef.value.validate()
    if (!valid) return
    if (new Date(rewardForm.value.effectiveTime).getTime() > new Date(rewardForm.value.endTime).getTime()) {
      errorNotification('End time cannot be smaller than effective date')
      return
    }
    if (rewardForm.value.criteria.daily) {
      const now = new Date()
      const effectiveTemp = new Date(rewardForm.value.effectiveTime)
      effectiveTemp.setHours(0, 0, 0, 0)
      now.setHours(0, 0, 0, 0)
      if (now.getTime() === effectiveTemp.getTime()) {
        const fromHourTemp = new Date()
        const toHourTemp = new Date()
        fromHourTemp.setHours(rewardForm.value.criteria.fromHour)
        toHourTemp.setHours(rewardForm.value.criteria.toHour)
        if (fromHourTemp.getTime() < new Date().getTime() || toHourTemp.getTime() < new Date().getTime()) {
          errorNotification('From & To hour cannot be in the past')
          return
        }
      }
      if (rewardForm.value.criteria.toHour < rewardForm.value.criteria.fromHour) {
        errorNotification('From hour cannot be greater than to hour')
        return
      }
    } else if (new Date(rewardForm.value.expireAt).getTime() < new Date(rewardForm.value.endTime).getTime()) {
      errorNotification('Expire at cannot be smaller than expiry date')
      return
    }
    submitting.value = true
    const temp = { ...rewardForm.value }
    temp.effectiveTime = new Date(temp.effectiveTime).toISOString()
    temp.endTime = new Date(temp.endTime).toISOString()
    if (temp.criteria.daily) {
      delete temp.criteria.fromDate
      delete temp.criteria.toDate
      delete temp.expireAt
    } else {
      delete temp.criteria.fromHour
      delete temp.criteria.toHour
      temp.expireAt = new Date(temp.expireAt).toISOString()
      temp.criteria.fromDate = temp.effectiveTime
      temp.criteria.toDate = temp.endTime
    }
    try {
      if (props.isNew) {
        await ctx.root.$store.dispatch('rewards/createReward', temp)
      } else {
        await ctx.root.$store.dispatch('rewards/patchReward', {
          data: temp,
          id: ctx.root.$route.params.id,
        })
      }
      ctx.root.$router.push('/rewards')
    } catch (error) {
      console.log(error)
      errorNotification('An error occured')
    } finally {
      submitting.value = false
    }
  }
  const numberOfTripsInput = ev => {
    setTimeout(() => {
      const temp = String(ev.key)
      if (temp === '+' || temp === 'e') rewardForm.value.criteria.numberOfTrips = 0
      if (!ev) rewardForm.value.criteria.numberOfTrips = 0
    }, 0)
  }
  return {
    groupsList,
    numberOfTripsInput,
    submitHandler,
    rewardsFormRef,
    fetchingGroups,
    rewardForm,
    submitting,
    roleOptions,
    fetchGroups,
    zonesList,
    fetchingZones,
    fetchingReward,
  }
}
